import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Plus } from 'lucide-react';
import PageWrapper from '@/components/layout/PageWrapper';
import DataTable, { type ColumnDef } from '@/components/shared/DataTable';
import StatusBadge from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { pmAssignmentsApi } from '@/api';
import { useAuth } from '@/auth/AuthContext';
import { formatDate, cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import type { PmAssignment } from '@/types';
import PmAssignmentForm from '@/components/pmTracking/PmAssignmentForm';

function getDaysUntilDue(nextDueDate: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(nextDueDate);
  due.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function getStatus(row: PmAssignment): 'overdue' | 'due_soon' | 'ok' {
  if (!row.nextDueDate) return 'ok';
  const days = getDaysUntilDue(row.nextDueDate);
  const alertDays = row.autoCreateDaysBeforeOverride ?? row.pmType?.alertDaysBefore ?? 7;
  if (days < 0) return 'overdue';
  if (days <= alertDays) return 'due_soon';
  return 'ok';
}

function DaysUntilDue({ days }: { days: number }) {
  const colorClass =
    days < 0
      ? 'text-red-600 dark:text-red-400 font-semibold'
      : days <= 7
      ? 'text-yellow-600 dark:text-yellow-400 font-semibold'
      : 'text-green-600 dark:text-green-400';

  return (
    <span className={cn(colorClass)}>
      {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}
    </span>
  );
}

export default function PMTracking() {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();
  const [creatingId, setCreatingId] = useState<number | null>(null);
  const [editingAssignment, setEditingAssignment] = useState<PmAssignment | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPmType, setFilterPmType] = useState<string>('all');
  const [filterEquipment, setFilterEquipment] = useState<string>('all');

  const canManage = hasRole(['pm_admin', 'super_admin', 'equipment_admin']);
  const canCreate = hasRole(['repair_tech', 'pm_admin', 'super_admin']);

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['pmAssignments', 'active'],
    queryFn: () => pmAssignmentsApi.list({ active: true }),
  });

  const pmTypeOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const a of assignments) {
      if (a.pmType) seen.set(a.pmType.id, a.pmType.name);
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [assignments]);

  const equipmentOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const a of assignments) {
      const label = a.equipment?.unitNumber ?? `#${a.equipmentId}`;
      seen.set(a.equipmentId, label);
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [assignments]);

  const STATUS_ORDER = { overdue: 0, due_soon: 1, ok: 2 };

  const filtered = useMemo(() => {
    return assignments
      .filter((a) => {
        if (filterStatus !== 'all' && getStatus(a) !== filterStatus) return false;
        if (filterPmType !== 'all' && String(a.pmTypeId) !== filterPmType) return false;
        if (filterEquipment !== 'all' && String(a.equipmentId) !== filterEquipment) return false;
        return true;
      })
      .sort((a, b) => STATUS_ORDER[getStatus(a)] - STATUS_ORDER[getStatus(b)]);
  }, [assignments, filterStatus, filterPmType, filterEquipment]);

  const createROMutation = useMutation({
    mutationFn: (id: number) => pmAssignmentsApi.createRepairOrder(id),
    onMutate: (id) => setCreatingId(id),
    onSettled: () => setCreatingId(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pmAssignments'] });
      toast({ title: 'Repair order created' });
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error ? err.message : 'Failed to create repair order';
      toast({ title: msg, variant: 'destructive' });
    },
  });

  const columns: ColumnDef<PmAssignment>[] = [
    {
      key: 'equipment',
      header: 'Unit',
      sortable: true,
      cell: (row) => (
        <span className="font-mono font-medium">
          {row.equipment?.unitNumber ?? `#${row.equipmentId}`}
        </span>
      ),
    },
    {
      key: 'pmType',
      header: 'PM Type',
      cell: (row) => (
        <div className="flex items-center gap-2">
          {row.pmType?.color && (
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: row.pmType.color }}
            />
          )}
          <span>{row.pmType?.name ?? '—'}</span>
        </div>
      ),
    },
    {
      key: 'nextDueDate',
      header: 'Next Due',
      sortable: true,
      cell: (row) => row.nextDueDate ? formatDate(row.nextDueDate) : '—',
    },
    {
      key: 'daysUntilDue',
      header: 'Days Until Due',
      sortable: true,
      cell: (row) =>
        row.nextDueDate ? <DaysUntilDue days={getDaysUntilDue(row.nextDueDate)} /> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'lastCompletedDate',
      header: 'Last Completed',
      cell: (row) => (
        <span className="text-muted-foreground">
          {row.lastCompletedDate ? formatDate(row.lastCompletedDate) : '—'}
        </span>
      ),
    },
    {
      key: 'autoCreate',
      header: 'Alert Days',
      cell: (row) => {
        const override = row.autoCreateDaysBeforeOverride;
        const def = row.pmType?.alertDaysBefore;
        if (override != null) {
          return <span>{override}d <span className="text-muted-foreground text-xs">(override)</span></span>;
        }
        return <span className="text-muted-foreground">{def != null ? `${def}d` : '—'}</span>;
      },
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => <StatusBadge status={getStatus(row)} />,
    },
    ...(canCreate
      ? [
          {
            key: 'actions',
            header: '',
            cell: (row: PmAssignment) => {
              const status = getStatus(row);
              if (status === 'ok') return null;
              const isPending = creatingId === row.id;
              return (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-blue-600 border-blue-200 hover:bg-blue-50 dark:text-blue-300 dark:border-blue-600 dark:hover:bg-blue-500/20"
                  disabled={isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    createROMutation.mutate(row.id);
                  }}
                >
                  <ClipboardList className="mr-1.5 h-3.5 w-3.5" />
                  {isPending ? 'Creating…' : 'Create Repair Order'}
                </Button>
              );
            },
          } as ColumnDef<PmAssignment>,
        ]
      : []),
  ];

  return (
    <PageWrapper
      title="PM Tracking"
      subtitle="Preventive maintenance assignments for all equipment"
      actions={
        canManage ? (
          <Button size="sm" onClick={() => setShowNewForm(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            New Assignment
          </Button>
        ) : undefined
      }
    >
      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="due_soon">Due Soon</SelectItem>
            <SelectItem value="ok">OK</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterPmType} onValueChange={setFilterPmType}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="PM Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All PM Types</SelectItem>
            {pmTypeOptions.map(([id, name]) => (
              <SelectItem key={id} value={String(id)}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterEquipment} onValueChange={setFilterEquipment}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Equipment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Equipment</SelectItem>
            {equipmentOptions.map(([id, label]) => (
              <SelectItem key={id} value={String(id)}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(filterStatus !== 'all' || filterPmType !== 'all' || filterEquipment !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setFilterStatus('all'); setFilterPmType('all'); setFilterEquipment('all'); }}
          >
            Clear filters
          </Button>
        )}
      </div>

      <DataTable<PmAssignment>
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        emptyMessage="No active PM assignments."
        keyExtractor={(row) => row.id}
        rowClassName={(row) => {
          const status = getStatus(row);
          return cn(
            canManage ? 'cursor-pointer' : '',
            status === 'overdue'
              ? 'bg-red-50/60 dark:bg-red-500/10'
              : status === 'due_soon'
              ? 'bg-yellow-50/60 dark:bg-yellow-500/10'
              : ''
          );
        }}
        onRowClick={canManage ? (row) => setEditingAssignment(row) : undefined}
      />

      {/* New assignment dialog */}
      <Dialog open={showNewForm} onOpenChange={(open) => { if (!open) setShowNewForm(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New PM Assignment</DialogTitle>
          </DialogHeader>
          <PmAssignmentForm
            onSuccess={() => setShowNewForm(false)}
            onCancel={() => setShowNewForm(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit assignment dialog */}
      <Dialog open={!!editingAssignment} onOpenChange={(open) => { if (!open) setEditingAssignment(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit PM Assignment</DialogTitle>
          </DialogHeader>
          {editingAssignment && (
            <PmAssignmentForm
              existing={editingAssignment}
              onSuccess={() => setEditingAssignment(null)}
              onCancel={() => setEditingAssignment(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </PageWrapper>
  );
}
